/**
 * @file Footer.ts
 * @description
 * -----------------------------------------------------
 * Archivo que contiene el componente de Footer
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2024-12-19
 * @modified 2025-11-03
 * @author DLTCode
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import useAppData from "@/hooks/useAppData";
import ImageComponent from "../ui/ImageComponent";
import UserManualLink from "../ui/UserManualLink";
import { useSession } from "@/features/auth";
import { usePathname } from "@/features/i18n";
import clsx from "clsx";
const Footer = () => {
  const $ = _c(115);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Date().getFullYear();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const year = t0;
  const {
    data: session
  } = useSession();
  const footerRef = useRef(null);
  const t = useTranslations("Footer");
  const appData = useAppData();
  usePathname();
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const footer = footerRef.current;
      if (!footer) {
        return;
      }
      const updateFooterHeight = () => {
        const height = footer.offsetHeight;
        document.documentElement.style.setProperty("--footer-height", `${height}px`);
      };
      updateFooterHeight();
      const resizeObserver = new ResizeObserver(updateFooterHeight);
      resizeObserver.observe(footer);
      return () => {
        resizeObserver.disconnect();
      };
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = clsx("footer");
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = appData.footer.logos.nextGenEU;
  let t5;
  if ($[4] !== t) {
    t5 = t("nextGenEU");
    $[4] = t;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== appData.footer.logos.nextGenEU || $[7] !== t5) {
    t6 = <Image src={t4} alt={t5} width={1080} height={720} />;
    $[6] = appData.footer.logos.nextGenEU;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = appData.footer.logos.minTdfp;
  let t8;
  if ($[9] !== t) {
    t8 = t("minTdfp");
    $[9] = t;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== appData.footer.logos.minTdfp || $[12] !== t8) {
    t9 = <Image src={t7} alt={t8} width={1080} height={720} />;
    $[11] = appData.footer.logos.minTdfp;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = appData.footer.logos.prtr;
  let t11;
  if ($[14] !== t) {
    t11 = t("prtr");
    $[14] = t;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== appData.footer.logos.prtr || $[17] !== t11) {
    t12 = <Image src={t10} alt={t11} width={1080} height={720} />;
    $[16] = appData.footer.logos.prtr;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const t13 = appData.footer.logos.incibe;
  let t14;
  if ($[19] !== t) {
    t14 = t("incibe");
    $[19] = t;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== appData.footer.logos.incibe || $[22] !== t14) {
    t15 = <Image src={t13} alt={t14} width={1080} height={720} />;
    $[21] = appData.footer.logos.incibe;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const t16 = appData.footer.logos.iso9001;
  let t17;
  if ($[24] !== t) {
    t17 = t("iso9001");
    $[24] = t;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== appData.footer.logos.iso9001 || $[27] !== t17) {
    t18 = <ImageComponent src={t16} alt={t17} width={160} height={192} zoomable={false} />;
    $[26] = appData.footer.logos.iso9001;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  const t19 = appData.footer.logos.iso27001;
  let t20;
  if ($[29] !== t) {
    t20 = t("iso27001");
    $[29] = t;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== appData.footer.logos.iso27001 || $[32] !== t20) {
    t21 = <ImageComponent src={t19} alt={t20} width={160} height={192} zoomable={false} />;
    $[31] = appData.footer.logos.iso27001;
    $[32] = t20;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  const t22 = appData.footer.logos.uneEn301549;
  let t23;
  if ($[34] !== t) {
    t23 = t("uneEn301549");
    $[34] = t;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== appData.footer.logos.uneEn301549 || $[37] !== t23) {
    t24 = <ImageComponent src={t22} alt={t23} width={160} height={192} zoomable={false} />;
    $[36] = appData.footer.logos.uneEn301549;
    $[37] = t23;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  const t25 = appData.footer.logos.validacionDnsh;
  let t26;
  if ($[39] !== t) {
    t26 = t("validacionDnsh");
    $[39] = t;
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  let t27;
  if ($[41] !== appData.footer.logos.validacionDnsh || $[42] !== t26) {
    t27 = <ImageComponent src={t25} alt={t26} width={160} height={192} zoomable={false} />;
    $[41] = appData.footer.logos.validacionDnsh;
    $[42] = t26;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  const t28 = appData.footer.logos.ens;
  let t29;
  if ($[44] !== t) {
    t29 = t("ens");
    $[44] = t;
    $[45] = t29;
  } else {
    t29 = $[45];
  }
  let t30;
  if ($[46] !== appData.footer.logos.ens || $[47] !== t29) {
    t30 = <ImageComponent src={t28} alt={t29} width={160} height={192} zoomable={false} />;
    $[46] = appData.footer.logos.ens;
    $[47] = t29;
    $[48] = t30;
  } else {
    t30 = $[48];
  }
  let t31;
  if ($[49] !== t18 || $[50] !== t21 || $[51] !== t24 || $[52] !== t27 || $[53] !== t30) {
    t31 = <section className="footer__images__aenor">{t18}{t21}{t24}{t27}{t30}</section>;
    $[49] = t18;
    $[50] = t21;
    $[51] = t24;
    $[52] = t27;
    $[53] = t30;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] !== t12 || $[56] !== t15 || $[57] !== t31 || $[58] !== t6 || $[59] !== t9) {
    t32 = <section className="footer__images">{t6}{t9}{t12}{t15}{t31}</section>;
    $[55] = t12;
    $[56] = t15;
    $[57] = t31;
    $[58] = t6;
    $[59] = t9;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  let t33;
  if ($[61] !== t) {
    t33 = t("linkedin");
    $[61] = t;
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  let t34;
  if ($[63] !== t) {
    t34 = t("opensInNewTab");
    $[63] = t;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  const t35 = `${t33} (${t34})`;
  let t36;
  if ($[65] !== t) {
    t36 = t("linkedin");
    $[65] = t;
    $[66] = t36;
  } else {
    t36 = $[66];
  }
  let t37;
  if ($[67] !== t35 || $[68] !== t36) {
    t37 = <li><Link href="https://www.linkedin.com/company/dlt-code" className="footer__link" target="_blank" rel="noopener noreferrer" aria-label={t35}>{t36}</Link></li>;
    $[67] = t35;
    $[68] = t36;
    $[69] = t37;
  } else {
    t37 = $[69];
  }
  let t38;
  if ($[70] !== t) {
    t38 = t("phone");
    $[70] = t;
    $[71] = t38;
  } else {
    t38 = $[71];
  }
  const t39 = `${t38}: +34 931 311 540`;
  let t40;
  if ($[72] !== t39) {
    t40 = <li><Link href="tel:+34931311540" aria-label={t39} className="footer__link">+34 931 311 540</Link></li>;
    $[72] = t39;
    $[73] = t40;
  } else {
    t40 = $[73];
  }
  let t41;
  if ($[74] !== t) {
    t41 = t("email");
    $[74] = t;
    $[75] = t41;
  } else {
    t41 = $[75];
  }
  const t42 = `${t41}: info@dltcode.es`;
  let t43;
  if ($[76] !== t42) {
    t43 = <li><Link href="mailto:info@dltcode.es" aria-label={t42} className="footer__link">info@dltcode.es</Link></li>;
    $[76] = t42;
    $[77] = t43;
  } else {
    t43 = $[77];
  }
  let t44;
  if ($[78] !== t37 || $[79] !== t40 || $[80] !== t43) {
    t44 = <ul className="footer__list">{t37}{t40}{t43}</ul>;
    $[78] = t37;
    $[79] = t40;
    $[80] = t43;
    $[81] = t44;
  } else {
    t44 = $[81];
  }
  let t45;
  if ($[82] !== t) {
    t45 = t("cookiesPolicy");
    $[82] = t;
    $[83] = t45;
  } else {
    t45 = $[83];
  }
  let t46;
  if ($[84] !== t) {
    t46 = t("opensInNewTab");
    $[84] = t;
    $[85] = t46;
  } else {
    t46 = $[85];
  }
  const t47 = `${t45} (${t46})`;
  let t48;
  if ($[86] !== t) {
    t48 = t("cookiesPolicy");
    $[86] = t;
    $[87] = t48;
  } else {
    t48 = $[87];
  }
  let t49;
  if ($[88] !== t47 || $[89] !== t48) {
    t49 = <li><Link href="https://dltcode.es/politica-cookies" className="footer__link" target="_blank" rel="noopener noreferrer" aria-label={t47}>{t48}</Link></li>;
    $[88] = t47;
    $[89] = t48;
    $[90] = t49;
  } else {
    t49 = $[90];
  }
  let t50;
  if ($[91] !== t) {
    t50 = t("privacyPolicy");
    $[91] = t;
    $[92] = t50;
  } else {
    t50 = $[92];
  }
  let t51;
  if ($[93] !== t) {
    t51 = t("opensInNewTab");
    $[93] = t;
    $[94] = t51;
  } else {
    t51 = $[94];
  }
  const t52 = `${t50} (${t51})`;
  let t53;
  if ($[95] !== t) {
    t53 = t("privacyPolicy");
    $[95] = t;
    $[96] = t53;
  } else {
    t53 = $[96];
  }
  let t54;
  if ($[97] !== t52 || $[98] !== t53) {
    t54 = <li><Link href="https://dltcode.es/politica-privacidad" className="footer__link" target="_blank" rel="noopener noreferrer" aria-label={t52}>{t53}</Link></li>;
    $[97] = t52;
    $[98] = t53;
    $[99] = t54;
  } else {
    t54 = $[99];
  }
  let t55;
  if ($[100] !== session) {
    t55 = session && <li><UserManualLink className="footer__link" /></li>;
    $[100] = session;
    $[101] = t55;
  } else {
    t55 = $[101];
  }
  let t56;
  if ($[102] !== t49 || $[103] !== t54 || $[104] !== t55) {
    t56 = <ul className="footer__list">{t49}{t54}{t55}</ul>;
    $[102] = t49;
    $[103] = t54;
    $[104] = t55;
    $[105] = t56;
  } else {
    t56 = $[105];
  }
  let t57;
  if ($[106] !== t) {
    t57 = t("footerIsotip");
    $[106] = t;
    $[107] = t57;
  } else {
    t57 = $[107];
  }
  let t58;
  if ($[108] !== t57) {
    t58 = <p className="footer__text"><ImageComponent src="/assets/img/footer_isotip.png" alt={t57} width={20} height={24} zoomable={false} />{" "}{year} - DLTCode</p>;
    $[108] = t57;
    $[109] = t58;
  } else {
    t58 = $[109];
  }
  let t59;
  if ($[110] !== t32 || $[111] !== t44 || $[112] !== t56 || $[113] !== t58) {
    t59 = <footer ref={footerRef} className={t3}>{t32}{t44}{t56}{t58}</footer>;
    $[110] = t32;
    $[111] = t44;
    $[112] = t56;
    $[113] = t58;
    $[114] = t59;
  } else {
    t59 = $[114];
  }
  return t59;
};
export default Footer;